import { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CheckCircle2, XCircle } from "lucide-react";
import { FAYTEX_FAQ_SECTIONS, FAYTEX_MYTHS } from "@/lib/faytexFaq";

export default function FaytexFAQ() {
  const [tab, setTab] = useState(FAYTEX_FAQ_SECTIONS[0].id);
  const activeSection = FAYTEX_FAQ_SECTIONS.find((s) => s.id === tab) || FAYTEX_FAQ_SECTIONS[0];

  return (
    <section data-testid="faytex-faq" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
      <div className="text-center">
        <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">Everything about Faytex</p>
        <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">
          Straight answers, no whispers.
        </h2>
        <p className="mt-3 text-faytex-muted max-w-xl mx-auto">
          From product range to myth-busting — click any question below.
        </p>
      </div>

      {/* Category tabs */}
      <div data-testid="faq-tabs" className="mt-10 flex flex-wrap justify-center gap-2">
        {FAYTEX_FAQ_SECTIONS.map((s) => (
          <button
            key={s.id}
            data-testid={`faq-tab-${s.id}`}
            onClick={() => setTab(s.id)}
            className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-widest transition-colors ${
              tab === s.id
                ? "bg-faytex-pink text-white"
                : "bg-faytex-pinkLight/60 text-faytex-pinkHover hover:bg-faytex-pink hover:text-white"
            }`}
          >
            {s.title}
          </button>
        ))}
      </div>

      {/* Accordion for active section */}
      <div data-testid={`faq-section-${activeSection.id}`} className="mt-8 bg-white rounded-3xl border border-gray-100 shadow-soft px-5 md:px-8 py-2">
        <Accordion type="single" collapsible>
          {activeSection.items.map((item, i) => (
            <AccordionItem
              key={i}
              value={`${activeSection.id}-${i}`}
              data-testid={`faq-item-${activeSection.id}-${i}`}
              className="border-b border-gray-100 last:border-b-0"
            >
              <AccordionTrigger className="text-left font-heading text-lg md:text-xl hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-faytex-muted leading-relaxed whitespace-pre-line text-sm md:text-base pb-5">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {/* Myths & Facts */}
      <div className="mt-16">
        <div className="text-center">
          <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">Faytex Myths & Facts</p>
          <h3 className="font-heading text-3xl md:text-4xl mt-3 tracking-tight">Ekaa. Let&apos;s clear the air.</h3>
        </div>
        <div data-testid="faq-myths" className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {FAYTEX_MYTHS.map((m, i) => (
            <article
              key={i}
              data-testid={`faq-myth-${i}`}
              className="rounded-2xl bg-white border border-gray-100 shadow-soft overflow-hidden"
            >
              <div className="p-5 border-b border-gray-100 flex gap-3">
                <XCircle className="w-5 h-5 flex-shrink-0 text-red-400 mt-0.5" />
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-red-400 font-semibold">Myth</p>
                  <p className="mt-1 text-faytex-ink">{m.myth}</p>
                </div>
              </div>
              <div className="p-5 bg-faytex-pinkLight/60 flex gap-3">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-faytex-pink mt-0.5" />
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-faytex-pink font-semibold">Fact</p>
                  <p className="mt-1 text-faytex-ink">{m.fact}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Our Promise closing card */}
      <div data-testid="faq-promise" className="mt-16 rounded-3xl bg-faytex-ink text-white p-8 md:p-12">
        <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">Our promise</p>
        <p className="mt-4 font-heading text-2xl md:text-3xl leading-snug">
          For more than 45 years, Faytex has supported women and girls with reliable menstrual care designed for comfort, confidence, and dignity.
        </p>
        <p className="mt-4 text-white/80 leading-relaxed">
          From first periods and regular monthly cycles to heavier-flow and maternity-care needs, Faytex is committed to providing suitable options for every stage of a woman&apos;s journey.
        </p>
        <p className="mt-6 font-heading text-lg md:text-xl text-faytex-pink">
          Proudly made in Ghana. Made for every flow. Made for every woman.
        </p>
        <p className="mt-2 text-sm text-white/70 italic">Every question matters. Every period matters.</p>
      </div>
    </section>
  );
}
